import React from 'react'
import { useAtomValue } from 'jotai'
import dt from 'dayjs'
import * as state from '../../states'
import {
	ERoleAccess,
	TRevenueSpendByTeam,
	ERevenueAgencyFakeType,
	TRevenueSpendByTeamRevenueCpa,
	TRevenueSpendByTeamRevenueSpend,
	TRevenueSpendByTeamRevenue,
	TRevenueSpendByTeamSpend,
	TRevenueSpendAgg,
	TRevenueSpendByTeamOfferSpend
} from '../../types'
import {
	UiTable,
	IUiTableConfig,
	headCellFormatterSortable,
	ESortOrder,
	UiTotalLabel
} from '../ui'
import { REVENUE_SPEND_BY_TEAM_PAGE_SORT_FIELDS, IRevenueSpendByTeamSortField } from '../../hooks'
import { Access } from '../../lib'
import * as helpers from '../../helpers'
import {
	renderByRevenue,
	getRevenueSpendTotalItemsAgg,
	renderBySpend,
	getRevenueSpendAgencyList
} from './RevenueSpendByAccountListTable'
import { renderTotal } from '../offerSpend'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'


type IRevenueSpendByTeamListTableProps = {
	items: TRevenueSpendByTeam[];
	skip: number;
	sortBy: IRevenueSpendByTeamSortField;
	setSortBy: (sortBy: IRevenueSpendByTeamSortField) => void;
	order: ESortOrder;
	setOrder: (order: ESortOrder) => void;
	onViewRevenueCpaDetails?: (item: TRevenueSpendByTeam | TRevenueSpendByTeamRevenueCpa, e: React.MouseEvent) => void;
	onViewRevenueSpendDetails?: (item: TRevenueSpendByTeam | TRevenueSpendByTeamRevenueSpend, e: React.MouseEvent) => void;
	onViewOfferSpendDetails?: (item: TRevenueSpendByTeam | TRevenueSpendByTeamOfferSpend, e: React.MouseEvent) => void;
	onViewRevenueDetails?: (item: TRevenueSpendByTeam | TRevenueSpendByTeamRevenue, e: React.MouseEvent) => void;
	onViewSpendDetails?: (item: TRevenueSpendByTeam | TRevenueSpendByTeamSpend, e: React.MouseEvent) => void;
	showEmptySpendAgencyColumns?: boolean;
}

const NO_DATA_CELL: React.ReactNode = '-'


export const RevenueSpendByTeamListTable = (props: IRevenueSpendByTeamListTableProps) => {
	const {
		items = [],
		skip = 0,
		sortBy,
		setSortBy,
		order,
		setOrder,
		onViewRevenueCpaDetails,
		onViewRevenueSpendDetails,
		onViewOfferSpendDetails,
		onViewRevenueDetails,
		onViewSpendDetails,
		showEmptySpendAgencyColumns = false
	} = props
	const { t } = useTranslation()
	const revenueAgencies = useAtomValue(state.revenueAgenciesAtom)
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)

	const agencyList = getRevenueSpendAgencyList(revenueAgencies, items, showEmptySpendAgencyColumns)
	//console.log('RevenueSpendByTeamListTable: ', { revenueAgencies, agencyList, items })

	const totalItemsAgg = getRevenueSpendTotalItemsAgg(items)

	const revenueSpendListTableConfig: IUiTableConfig<TRevenueSpendByTeam>[] = [
		{
			key: 'at',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_date),
			classesToAdd: {
				th: 'text-center border-end',
				td: 'text-center border-end'
			},
			rowCellFormatter: ({ rowData }) => {
				if (!rowData) {
					return ''
				}

				const at = rowData?.at ?? null

				return <>
					{dt.utc(at).isValid() ? dt.utc(at).format('L') : '-'}
				</>
			}
		},
		{
			key: 'teamReport',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_team),
			classesToAdd: {
				th: 'text-center border-end',
				td: 'text-center border-end'
			},
			rowCellFormatter: ({ rowData }) => {
				if (!rowData) {
					return <UiTotalLabel/>
				}

				const teamReport = rowData?.teamReport ?? '?'

				return <>
					{teamReport}
				</>
			}
		},
		{
			key: 'revenueCpaTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_revenue_cpa),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const className = 'text-success'

				if (!rowData) {
					const total = totalItemsAgg.revenueCpaTotal
					return renderTotal(total, rowIndex, cellIndex, className)
				}
				const total = rowData.revenueCpaTotal ?? 0
				return renderByRevenue($access.$(ERoleAccess.RevenueSpendByTeamList), rowData, onViewRevenueCpaDetails)(total, rowIndex, cellIndex, className)
			}
		},
		/*{
			key: 'revenueSpendTotal',
			title: 'Revenue Spend',
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const className = 'text-success'

				if (!rowData) {
					const total = totalItemsAgg.revenueSpendTotal
					return renderTotal(total, rowIndex, cellIndex, className)
				}
				const total = rowData.revenueSpendTotal ?? 0
				return renderByRevenue($access.$(ERoleAccess.RevenueSpendByTeamList), rowData, onViewRevenueSpendDetails)(total, rowIndex, cellIndex, className)
			}
		},*/
		{
			key: 'offerSpendTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_revenue_offer_spend),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const className = 'text-success'

				if (!rowData) {
					const total = totalItemsAgg.offerSpendTotal
					return renderTotal(total, rowIndex, cellIndex, className)
				}
				const total = rowData.offerSpendTotal ?? 0
				return renderByRevenue($access.$(ERoleAccess.RevenueSpendByTeamList), rowData, onViewOfferSpendDetails)(total, rowIndex, cellIndex, className)
			}
		},
		{
			key: 'revenueTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_revenue_total),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const className = 'text-success'

				if (!rowData) {
					const total = totalItemsAgg.revenueCpaTotal + totalItemsAgg.offerSpendTotal
					return renderTotal(total, rowIndex, cellIndex, className)
				}
				const total = (rowData.revenueCpaTotal ?? 0) + (rowData.offerSpendTotal ?? 0)
				return renderTotal(total, rowIndex, cellIndex, className)
			}
		},
		{
			key: 'spendTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_spend),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const className = 'text-danger'
				const filterFunc = (item: TRevenueSpendAgg) => {
					return item.agency?.fakeType === ERevenueAgencyFakeType.None
				}

				if (!rowData) {
					const total = totalItemsAgg.spendTotal
					return renderTotal(total, rowIndex, cellIndex, className)
				}

				const total = rowData.spendTotal ?? 0
				const rowDataFiltered: TRevenueSpendByTeam = {
					...rowData,
					spend: rowData.spend.filter(filterFunc)
				}
				//console.log('spendTotal --- rowCellFormatter --- spendTotal ---', { rowData, rowDataFiltered })
				return renderBySpend($access.$(ERoleAccess.RevenueSpendByTeamList), rowDataFiltered, onViewSpendDetails)(total, rowIndex, cellIndex, className)
			}
		},
		{
			key: 'roi',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_roi),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				if (!rowData) {
					const total = totalItemsAgg.roi
					const className = total >= 0 ? 'text-success' : 'text-danger'
					return renderTotal(total, rowIndex, cellIndex, className)
				}
				const total = rowData.roi ?? 0
				const className = total >= 0 ? 'text-success' : 'text-danger'
				return renderTotal(total, rowIndex, cellIndex, className)
			}
		},
		{
			key: 'accountFBTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_account_fb),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const filterFunc = (item: TRevenueSpendAgg) => {
					return item.agency?.fakeType === ERevenueAgencyFakeType.AccountFB
				}

				if (!rowData) {
					const total = totalItemsAgg.accountFBTotal

					if (!total) {
						return NO_DATA_CELL
					}

					return renderTotal(total, rowIndex, cellIndex)
				}

				const total = rowData.accountFBTotal ?? 0
				if (!total) {
					return NO_DATA_CELL
				}

				const rowDataFiltered: TRevenueSpendByTeam = {
					...rowData,
					spend: rowData.spend.filter(filterFunc)
				}
				//console.log('accountFBTotal --- rowCellFormatter --- spendTotal ---', { rowData, rowDataFiltered })
				return renderBySpend($access.$(ERoleAccess.RevenueSpendByTeamList), rowDataFiltered, onViewSpendDetails)(total, rowIndex, cellIndex)
			}
		},
		{
			key: 'creoTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_creo),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const filterFunc = (item: TRevenueSpendAgg) => {
					return item.agency?.fakeType === ERevenueAgencyFakeType.Creo
				}

				if (!rowData) {
					const total = totalItemsAgg.creoTotal

					if (!total) {
						return NO_DATA_CELL
					}

					return renderTotal(total, rowIndex, cellIndex)
				}

				const total = rowData.creoTotal ?? 0
				if (!total) {
					return NO_DATA_CELL
				}

				const rowDataFiltered: TRevenueSpendByTeam = {
					...rowData,
					spend: rowData.spend.filter(filterFunc)
				}
				//console.log('creoTotal --- rowCellFormatter --- spendTotal ---', { rowData, rowDataFiltered })
				return renderBySpend($access.$(ERoleAccess.RevenueSpendByTeamList), rowDataFiltered, onViewSpendDetails)(total, rowIndex, cellIndex)
			}
		},
		{
			key: 'installsTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_installs),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const filterFunc = (item: TRevenueSpendAgg) => {
					return item.agency?.fakeType === ERevenueAgencyFakeType.Installs
				}

				if (!rowData) {
					const total = totalItemsAgg.installsTotal

					if (!total) {
						return NO_DATA_CELL
					}

					return renderTotal(total, rowIndex, cellIndex)
				}

				const total = rowData.installsTotal ?? 0
				if (!total) {
					return NO_DATA_CELL
				}

				const rowDataFiltered: TRevenueSpendByTeam = {
					...rowData,
					spend: rowData.spend.filter(filterFunc)
				}
				//console.log('installsTotal --- rowCellFormatter --- spendTotal ---', { rowData, rowDataFiltered })
				return renderBySpend($access.$(ERoleAccess.RevenueSpendByTeamList), rowDataFiltered, onViewSpendDetails)(total, rowIndex, cellIndex)
			}
		},
		{
			key: 'slicesTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_slices),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const filterFunc = (item: TRevenueSpendAgg) => {
					return item.agency?.fakeType === ERevenueAgencyFakeType.Slices
				}

				if (!rowData) {
					const total = totalItemsAgg.slicesTotal

					if (!total) {
						return NO_DATA_CELL
					}

					return renderTotal(total, rowIndex, cellIndex)
				}

				const total = rowData.slicesTotal ?? 0
				if (!total) {
					return NO_DATA_CELL
				}

				const rowDataFiltered: TRevenueSpendByTeam = {
					...rowData,
					spend: rowData.spend.filter(filterFunc)
				}
				//console.log('slicesTotal --- rowCellFormatter --- spendTotal ---', { rowData, rowDataFiltered })
				return renderBySpend($access.$(ERoleAccess.RevenueSpendByTeamList), rowDataFiltered, onViewSpendDetails)(total, rowIndex, cellIndex)
			}
		},
		{
			key: 'agencyPercentTotal',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_agency_percent),
			classesToAdd: {
				th: 'text-center border-end',
				td: 'text-center border-end'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const filterFunc = (item: TRevenueSpendAgg) => {
					return item.agency?.fakeType === ERevenueAgencyFakeType.None
				}

				if (!rowData) {
					const total = totalItemsAgg.agencyPercentTotal
					return renderTotal(total, rowIndex, cellIndex)
				}

				const total = rowData.agencyPercentTotal ?? 0
				const rowDataFiltered: TRevenueSpendByTeam = {
					...rowData,
					spend: rowData.spend.filter(filterFunc)
				}
				//console.log('agencyPercentTotal --- rowCellFormatter --- spendTotal ---', { rowData, rowDataFiltered })
				return renderBySpend($access.$(ERoleAccess.RevenueSpendByTeamList), rowDataFiltered, onViewSpendDetails)(total, rowIndex, cellIndex)
			}
		},
		{
			key: 'profit',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_list_table.column_profit),
			classesToAdd: {
				th: 'text-center border-end',
				td: 'text-center border-end'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				if (!rowData) {
					const total = totalItemsAgg.profit
					const className = total >= 0 ? 'text-success' : 'text-danger'
					return renderTotal(total, rowIndex, cellIndex, className)
				}
				const total = rowData.profit ?? 0
				const className = total >= 0 ? 'text-success' : 'text-danger'
				return renderTotal(total, rowIndex, cellIndex, className)
			}
		},

		...(agencyList.map((agencyItem, agencyItemIndex): IUiTableConfig<TRevenueSpendByTeam> => {
			const agencyId = agencyItem?.id
			const agencyName = agencyItem?.name

			return {
				key: `agency-spend-${agencyName}-${agencyId ?? agencyItemIndex}`,
				title: `${agencyName}`,
				classesToAdd: {
					th: 'text-center',
					td: 'text-center'
				},
				rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
					const filterFunc = (item: TRevenueSpendAgg) => {
						return item.agencyId === agencyId
					}

					if (!rowData) {
						let total = 0
						items.forEach(rowDataItem => {
							const spendAmountByAgency = rowDataItem?.spend?.filter(filterFunc)
								.reduce((sum, item) => sum + (item?.amount ?? 0), 0)

							total += spendAmountByAgency
						})

						if (!total) {
							return NO_DATA_CELL
						}
						//console.log('agencySpendTotal --- TOTAL --- rowCellFormatter --- spendTotal ---', { agencyItem, rowData })
						return renderTotal(total, rowIndex, cellIndex)
					}

					const spendAmountByAgency = rowData?.spend?.filter(filterFunc)
						.reduce((sum, item) => sum + (item?.amount ?? 0), 0)
					if (!spendAmountByAgency) {
						return NO_DATA_CELL
					}

					const rowDataFiltered: TRevenueSpendByTeam = {
						...rowData,
						spend: rowData.spend.filter(filterFunc)
					}
					//console.log('agencySpendTotal --- rowCellFormatter --- spendTotal ---', { agencyItem, rowData, rowDataFiltered })
					return renderBySpend($access.$(ERoleAccess.RevenueSpendByTeamList), rowDataFiltered, onViewSpendDetails)(spendAmountByAgency, rowIndex, cellIndex)
				}
			}
		}))
	]

	const revenueSpendListTableConfigSortable: IUiTableConfig<TRevenueSpendByTeam>[] = revenueSpendListTableConfig.map(
		(configItem) => {
			const configItemIsSortable = REVENUE_SPEND_BY_TEAM_PAGE_SORT_FIELDS.includes(configItem.key as IRevenueSpendByTeamSortField)

			if (!configItemIsSortable) {
				return configItem
			} else {
				return {
					...configItem,
					isSortable: true,
					headCellFormatter: ({
						value,
						configItem,
						cellIndex
					}) => {

						return headCellFormatterSortable({
							value,
							configItem,
							cellIndex,
							sortBy,
							setSortBy,
							order,
							setOrder
						})
					}
				}
			}
		}
	)

	//console.log('RevenueSpendByTeamListTable:: ', { revenueSpendListTableConfig, revenueSpendListTableConfigSortable })
	const { dataItems, tableClassName } = helpers.getTableItemsWithTotalRows(items)

	return <>
		<UiTable
			data={dataItems}
			config={revenueSpendListTableConfigSortable}
			// TODO: uncomment if necessary
			classesToAdd={{
				// wrapper: 'table-sticky-wrapper table-sticky-wrapper--top-87',
				table: tableClassName
			}}
		/>
	</>
}